<template>
  <div class="roster">

    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-title">
        <div class="site-line">
          <span class="country-chip">{{ countryCode }}</span>
          <h1 class="title">{{ heading }}</h1>
        </div>
      </div>

      <div class="toolbar-right">
        <div class="controls">
          <div class="search">
            <input
              v-model="query"
              type="search"
              placeholder="Search team member..."
              aria-label="Search team member"
            >
          </div>

          <label class="filter">
            <span class="filter-label">Filter:</span>
            <select v-model="filter">
              <option value="all">All</option>
              <option value="in">In building</option>
              <option value="out">Away</option>
            </select>
          </label>
        </div>

        <div v-if="!loading && !error" class="counts">
          <div class="count">
            <span class="count-n count-in">{{ present.length }}</span>
            <span class="count-label">Available</span>
          </div>
          <div class="count-divider"></div>
          <div class="count">
            <span class="count-n">{{ away.length }}</span>
            <span class="count-label">Away</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading">
      <span class="pulse-node"></span>
      <span>Reading the roster</span>
    </div>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="people.length === 0" class="msg">
      <template v-if="officeName">
        Nobody has {{ officeName }} set as their office in {{ countryLabel }}
      </template>
      <template v-else>
        Nobody in {{ countryLabel }} has an office set in Entra
      </template>
    </p>

    <p v-else-if="visible.length === 0" class="msg">
      No matches
      <button class="link" @click="clearFilters">Clear filters</button>
    </p>

    <ul v-else class="grid">
      <li v-for="person in visible" :key="person.id">
        <router-link
          class="card"
          :class="cardClass(person)"
          :to="{ name: 'PersonLog', query: { user_id: person.id, email: person.email, name: person.name } }"
        >

          <div class="card-head">
            <div class="avatar">
              <img v-if="photos[person.id]" :src="photos[person.id]" :alt="person.name">
              <span v-else class="initials">{{ initials(person.name) }}</span>
            </div>

            <div class="identity">
              <h2 class="name">{{ person.name }}</h2>
              <p class="role">{{ person.department || person.jobTitle || '\u2014' }}</p>
              <!-- Only when it differs from the site being viewed; saying
                   "at Birkerod" on the Birkerod page is noise. -->
              <!-- Where they are, when the door knows and it is not the page
                   you are already looking at. In country mode their own office
                   is shown instead, since the page spans several. -->
              <p v-if="person.atOffice && person.atOffice !== officeName" class="elsewhere">
                at {{ person.atOffice }}
              </p>
              <p v-else-if="!officeName && person.homeOffice" class="home-office">
                {{ person.homeOffice }}
              </p>
            </div>

            <!-- The door wins over a status set earlier.
                 A status is an intention; a badge event is evidence. Somebody
                 who said "out of office" and then badged into Singapore is in
                 Singapore, and showing the old status would say they are hard
                 to reach when they are standing in the building. -->
            <span v-if="person.checkedIn && person.atOffice" class="pill">
              at {{ person.atOffice }}
            </span>

            <span v-else-if="person.statusLabel" class="pill">
              <span class="pill-icon">{{ person.statusIcon }}</span>
              {{ person.statusLabel }}
            </span>
          </div>


        </router-link>
      </li>
    </ul>

  </div>
</template>


<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { getPeopleAtSite, getPeopleInCountry, loadPhotos, initials, countryName } from '@/services/GraphService'
import { getStatusTypes, getStatusesToday, statusMeta } from '@/services/StatusService'
import { getCurrentPresence, seenToday, zoneFor } from '@/services/PresenceService'
import { statusActiveNow } from '@/services/localTime'


const route = useRoute()

const countryCode = computed(() => route.params.country)
const countryLabel = computed(() => countryName(countryCode.value))

// The office parameter is optional. Without it the page lists everyone in the
// country, which is what the overview links to now: filtering by office turned
// out to answer a question nobody was asking, since a person's Entra office is
// where they sit, not where they are.
const officeName = computed(() =>
  route.params.office ? decodeURIComponent(route.params.office) : ''
)

const heading = computed(() => officeName.value || countryLabel.value)

const people = ref([])
const photos = ref({})
const loading = ref(true)
const error = ref(null)

const query = ref('')
const filter = ref('all')

// Nobody counts as present while there is no check-in data. The filter still
// separates people who have set a status from those who have not, which is the
// only distinction Vero can actually make today.
/**
 * How a card reads. Three states, not two.
 *
 * Somebody working from home is not absent: they told us where they are, which
 * is more than the people simply not badged in have done. Dimming them put them
 * with the unaccounted-for, and made setting a status look like a demotion.
 *
 * Kept in step with the same rule on the display, which is where people
 * actually notice the difference.
 */
function cardClass(person) {
  if (person.checkedIn) return 'is-in'
  if (person.statusIcon) return 'has-status'
  return 'is-out'
}

const present = computed(() => people.value.filter((p) => p.checkedIn))
const away = computed(() => people.value.filter((p) => !p.checkedIn))

const visible = computed(() => {
  const q = query.value.trim().toLowerCase()

  return people.value
    .filter((p) => {
      if (filter.value === 'in') return p.checkedIn
      if (filter.value === 'out') return !p.checkedIn
      return true
    })
    .filter((p) => {
      if (!q) return true
      return [p.name, p.department, p.jobTitle, p.email]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q))
    })
    // People who are in sort first, so the grid opens with the answer to
    // "who is here".
    .sort((a, b) => {
      if (a.checkedIn !== b.checkedIn) return a.checkedIn ? -1 : 1
      return a.name.localeCompare(b.name)
    })
})

function clearFilters() {
  query.value = ''
  filter.value = 'all'
}

let cancelled = false

async function load() {
  loading.value = true
  error.value = null

  try {
    let result

    if (officeName.value) {
      result = await getPeopleAtSite(countryCode.value, officeName.value)
    } else {
      // Everyone in the country, in one call.
      //
      // This used to pool the offices one at a time, which meant anybody with a
      // blank officeLocation was never fetched. That field is sparsely filled,
      // so a country roster was quietly missing most of its people.
      const people = await getPeopleInCountry(countryCode.value)
      if (cancelled) return

      result = (people ?? []).map((p) => ({ ...p, homeOffice: p.office }))
    }

    if (result === null) return
    if (cancelled) return

    const [statuses, presence] = await Promise.all([
      getStatusesToday().catch(() => ({})),
      getCurrentPresence().catch(() => ({})),

      // The status definitions, so statusMeta can name a status somebody added
      // under Admin > Statuses. Appended rather than inserted: it is not
      // destructured, and inserting would shift every result after it.
      getStatusTypes().catch(() => [])
    ])

    people.value = result.map((p) => {
      // Only a status in force right now, in this country's own clock. One
      // scheduled for the afternoon has not started this morning.
      const status = statuses[p.id]
      const meta = statusActiveNow(status, zoneFor(countryCode.value))
        ? statusMeta(status.status)
        : null
      // Today's event, in the country's own day: an event from yesterday says
      // nothing about where somebody is now.
      const raw = presence[p.id] ?? null
      const here = raw && seenToday(raw, zoneFor(countryCode.value)) ? raw : null

      return {
        ...p,
        statusIcon: meta?.icon ?? null,
        statusLabel: meta?.label ?? null,

        // The DIRECTION decides, not merely having a row.
        //
        // The presence endpoint returns exits as well as entries, so somebody
        // who came in and left can still be shown as having been here today.
        // Treating any row as "inside" made an exit read as an arrival: the
        // display had this person out, the roster had them in, and their log
        // showed the exit that proved the display right.
        checkedIn: here?.direction === 'ENTERED',

        // Where they actually are, which is not always the office they belong
        // to. Only meaningful while they are inside.
        atOffice: here?.direction === 'ENTERED' ? (here.office_name ?? null) : null,

        // Away means: has a status set AND is not currently in a building.
        //
        // Not "&& !here": after they badge out, here is still today's event, so
        // that would keep them grouped with the present. A checkout returns
        // them to their status, because the trip is still on.
        away: Boolean(meta) && here?.direction !== 'ENTERED'
      }
    })

    // Photos stream in one by one rather than blocking the grid, since each is
    // its own request and many will 404.
    loadPhotos(
      people.value.map((p) => p.id),
      (id, url) => { if (!cancelled) photos.value = { ...photos.value, [id]: url } }
    )
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    if (!cancelled) loading.value = false
  }
}

onMounted(load)
onBeforeUnmount(() => { cancelled = true })
</script>


<style scoped>
.roster {
  /* Epoka's own navy rather than a generic blue, so the page stays on brand. */
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #e2e8f0;
  --line-strong: #cbd5e1;
  --wash: #f8fafc;
  --paper: #ffffff;
  --in: #16a34a;
  --in-bg: #f0fdf4;
  --in-text: #15803d;

  /* Left aligned, not centred. Centring made the content drift right of the
     menu and the logo, so nothing lined up with anything else. The max-width
     stays: it stops a line of text running the width of a wide monitor. */
  max-width: 1560px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #0f172a;
}

/* ---------- Toolbar ---------- */

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  padding: 32px 0 10px;
}


.site-line {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
}

.country-chip {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--paper);
  background: var(--ink);
  padding: 6px 11px;
  border-radius: 8px;
}

.title {
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0;
  color: var(--ink);
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search input {
  font-family: inherit;
  font-size: 14px;
  color: #0f172a;
  width: 250px;
  padding: 11px 16px;
  border: 1px solid var(--line-strong);
  border-radius: 10px;
  background: var(--paper);
  outline: none;
}

.search input::placeholder { color: #94a3b8; }
.search input:focus { border-color: var(--ink); }

.filter {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--line-strong);
  border-radius: 10px;
  padding: 0 14px;
  height: 43px;
  background: var(--paper);
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.filter select {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  border: none;
  background: none;
  outline: none;
  cursor: pointer;
  padding: 0;
}

/* Headline counts, lifted from the reference: two big numbers in a boxed
   panel so the answer to "how many are here" is readable across a room. */
.counts {
  display: flex;
  align-items: center;
  gap: 22px;
  background: var(--wash);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 10px 26px;
}

.count {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.count-n {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--ink-soft);
}

.count-n.count-in { color: var(--in); }

.count-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin-top: 2px;
}

.count-divider {
  width: 1px;
  height: 38px;
  background: var(--line-strong);
}

/* ---------- Loading and messages ---------- */

.loading {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 32px 0;
}

.pulse-node {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--accent);
  animation: pulse 1.1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.2; }
}

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 15px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  margin-left: 10px;
  text-decoration: underline;
  cursor: pointer;
}

/* ---------- Grid ---------- */

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(268px, 1fr));
  gap: 16px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.grid > li {
  display: flex;
  min-width: 0;
}

.card {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: transform 0.25s ease, box-shadow 0.25s ease, opacity 0.25s ease;
}

.card:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

/* People who are out recede: lighter card, no lift on the eye. */
.card.is-out {
  background: var(--wash);
  opacity: 0.82;
}

.card.is-out:hover {
  opacity: 1;
  transform: none;
}

/* ---------- Card head ---------- */

.card-head {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 16px;
}

.avatar {
  position: relative;
  flex: none;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(0, 60, 108, 0.08);
  display: grid;
  place-items: center;
  box-shadow: 0 0 0 2px var(--paper), inset 0 1px 3px rgba(15, 23, 42, 0.08);
}

.is-out .avatar {
  background: #e2e8f0;
  box-shadow: 0 0 0 2px var(--wash), inset 0 1px 3px rgba(15, 23, 42, 0.08);
}

.avatar img {
  width: 56px;
  height: 56px;
  object-fit: cover;
  display: block;
}

/* Colour for people who are in, greyscale for people who are out. The fastest
   read on the card: the grid can be scanned on photo alone. */
.is-out .avatar img {
  filter: grayscale(1);
}

.initials {
  font-size: 19px;
  font-weight: 700;
  color: var(--ink);
}

.is-out .initials { color: #64748b; }

.identity { min-width: 0; flex: 1; }

.name {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.015em;
  line-height: 1.25;
  margin: 0;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.is-out .name { color: #475569; }

.home-office {
  font-size: 12.5px;
  color: var(--ink-soft);
  margin: 3px 0 0;
}

.elsewhere {
  font-size: 12.5px;
  font-weight: 600;
  color: #0f8b6c;
  margin: 3px 0 0;
}

.role {
  font-size: 13.5px;
  color: var(--ink-soft);
  margin: 4px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---------- Card foot ---------- */

.pill {
  flex: none;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 6px;
}


.is-out .pill {
  background: #e2e8f0;
  color: #475569;
}

.pill-icon { font-size: 12px; line-height: 1; }



@keyframes blink {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
}


/* ---------- Narrow screens ---------- */

@media (max-width: 900px) {
  .roster { padding: 0 20px 60px; }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-right {
    justify-content: space-between;
  }

  .search input { width: 100%; }
  .search { flex: 1; }
}

@media (max-width: 560px) {
  .grid { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); }
  .controls { flex-direction: column; align-items: stretch; }
  .title { font-size: 27px; }
}

@media (prefers-reduced-motion: reduce) {
  .card { transition: none; }
  .pulse-node { animation: none; }
}
</style>
